export interface PageInfoConfig {
  title: string;
  /** Context shown under the title for the current route. */
  pageDescription: string;
}

export const DEFAULT_PAGE_INFO: PageInfoConfig = {
  title: 'Sustainow Carbon Intelligence',
  pageDescription:
    'Activity data, documents, and transactions are reconciled with verified emission factors so footprint results stay consistent across modules.',
};

const DOCUMENT_TITLE_BY_PATH: Record<string, string> = {
  '/': 'Carbon Intelligence overview',
  '/login': 'Sign in to Carbon Intelligence',
  '/register': 'Create account',
  '/forgot-password': 'Forgot password',
  '/reset-password': 'Reset password',
  '/privacy-policy': 'Privacy policy',
  '/terms-of-service': 'Terms of service',
  '/help': 'Help center',
  '/about-us': 'About us',
  '/why-carbon-intelligence': 'Why Carbon Intelligence?',
  '/why-sci': 'Why Carbon Intelligence?',
  '/why-ci': 'Why Carbon Intelligence?',
  '/pricing': 'Pricing & plans',
  '/insights': 'Insights & articles',
  '/billing/terms-and-conditions': 'Billing terms and conditions',
  '/billing/privacy-policy': 'Billing privacy policy',
  '/billing/refund-policy': 'Billing refund policy',
  '/billing/cancellation-policy': 'Billing cancellation policy',
  '/admin/login': 'Sign in to Carbon Intelligence',
  '/partners': 'Partners',
  '/partners/api-documentation': 'Partner API documentation',
  '/developers': 'Partners',
  '/partner/login': 'Sign in to Carbon Intelligence',
  '/partner/dashboard': 'Partner dashboard',
};

const ROUTE_PAGE_INFO_ENTRIES: Array<{ match: RegExp; config: PageInfoConfig }> = [
  {
    match: /^\/msme-registration/,
    config: {
      title: 'MSME registration',
      pageDescription:
        'Complete company and operational details so carbon models and programs use an accurate baseline.',
    },
  },
  {
    match: /^\/admin\/emission-factors/,
    config: {
      title: 'Emission factor review',
      pageDescription:
        'Open the monthly factor review, approve or reject each change, and publish the factors calculations use.',
    },
  },
  {
    match: /^\/admin\/integrations/,
    config: {
      title: 'Integration APIs',
      pageDescription:
        'Provision partner API keys and review the public catalog for banks, auditors, and third-party integrators.',
    },
  },
  {
    match: /^\/admin\/dashboard/,
    config: {
      title: 'Admin dashboard',
      pageDescription: 'Review MSME accounts, footprint status, and platform activity.',
    },
  },
  {
    match: /^\/partner\/dashboard/,
    config: {
      title: 'Partner dashboard',
      pageDescription:
        'Track API usage, success rates, billing estimates, and endpoint statistics for your integration.',
    },
  },
  {
    match: /^\/partner\/login/,
    config: {
      title: 'Partner portal sign in',
      pageDescription: 'Sign in to monitor API usage, billing, and integration statistics.',
    },
  },
  {
    match: /^\/partners\/api-documentation/,
    config: {
      title: 'Public API documentation',
      pageDescription:
        'Authentication, scopes, and REST endpoints for partner integrations including usage metering and webhooks.',
    },
  },
  {
    match: /^\/(partners|developers)/,
    config: {
      title: 'Partners',
      pageDescription:
        'Partnership pricing, commercial models, and REST API documentation for banks, auditors, and integrators.',
    },
  },
  {
    match: /^\/insights\/[^/]+$/,
    config: {
      title: 'Insight article',
      pageDescription: 'Sustainability and carbon intelligence guidance from Sustainow.',
    },
  },
  {
    match: /^\/insights$/,
    config: {
      title: 'Insights & articles',
      pageDescription: 'Practical guidance on carbon measurement, compliance, and MSME sustainability.',
    },
  },
  {
    match: /^\/admin\/blog-articles/,
    config: {
      title: 'Blog & articles',
      pageDescription: 'Create and publish insights for the public pre-login dashboard.',
    },
  },
  {
    match: /^\/admin\/msme\//,
    config: {
      title: 'MSME record',
      pageDescription: 'Inspect profile, documents, and emissions signals for this account.',
    },
  },
  {
    match: /^\/expert\/dashboard/,
    config: {
      title: 'Expert emission review',
      pageDescription: 'Classify vouchers held out of the inventory until an activity quantity is named.',
    },
  },
  {
    match: /^\/support\/dashboard/,
    config: {
      title: 'Customer Support Dashboard',
      pageDescription: 'Monitor MSME account status, usage, issues, and resolutions.',
    },
  },
  {
    match: /^\/support\/msme\//,
    config: {
      title: 'Support MSME account',
      pageDescription: 'Read-only account status, usage, and resolution context for customer support.',
    },
  },
  {
    match: /^\/msme-profile(\/|$)/,
    config: {
      title: 'Company profile',
      pageDescription:
        'Registration, organizational boundary, and manufacturing profile details that anchor downstream carbon assessments.',
    },
  },
  {
    match: /^\/operations\/units/,
    config: {
      title: 'Manufacturing units',
      pageDescription:
        'Manufacturing units, production capacity, and GPS. Energy and monthly meters stay on Energy and meters.',
    },
  },
  {
    match: /^\/operations\/scope-1/,
    config: {
      title: 'Direct emissions',
      pageDescription: 'Direct emissions from fuel and process sources at the plant.',
    },
  },
  {
    match: /^\/operations\/scope-3/,
    config: {
      title: 'Value chain',
      pageDescription: 'Purchased goods, transport, and other value-chain activity.',
    },
  },
  {
    match: /^\/buyer-audit/,
    config: {
      title: 'Buyer audit pack',
      pageDescription:
        'Respond to buyer and bank audit requests from one place — track deadlines, gather evidence, and share reports.',
    },
  },
  {
    match: /^\/operations/,
    config: {
      title: 'Energy and meters',
      pageDescription:
        'Monthly electricity, fuel, and water, the electricity supplier, and purchased-electricity contracts.',
    },
  },
  {
    match: /^\/carbon-twin/,
    config: {
      title: 'Carbon picture',
      pageDescription:
        'A picture of the company from its activity, hotspots, reduction ideas, and supplier scores.',
    },
  },
  {
    match: /^\/dashboard\/?$/,
    config: {
      title: 'Home',
      pageDescription:
        'Your carbon score, and the path through the workspace.',
    },
  },
  {
    match: /^\/carbon-footprint/,
    config: {
      title: 'Carbon footprint',
      pageDescription:
        'Per-transaction factors (grid, fuels, location, profile) and category passes combine for higher-fidelity kg CO₂e totals.',
    },
  },
  {
    match: /^\/emissions-reduction-targets/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'Measured savings, the cut you set, government scheme sites, and offset trading for what remains.',
    },
  },
  {
    match: /^\/compliance\/india/,
    config: {
      title: 'India market',
      pageDescription:
        'BRSR Core supplier packs, ISO 14064 assurance, PAT-style intensity, SBTi targets, product footprints, and Indian carbon market alignment.',
    },
  },
  {
    match: /^\/compliance\/export/,
    config: {
      title: 'Global export',
      pageDescription:
        'CBAM, CSRD, CDP, TCFD/ISSB S2, buyer questionnaires, EUDR, and EcoVadis exports for international customers.',
    },
  },
  {
    match: /^\/compliance/,
    config: {
      title: 'Compliance',
      pageDescription:
        'Scoped MSME reporting prep: BRSR Principle 6, ISO assurance, CBAM monitoring, and buyer supplier packs — not full statutory filing.',
    },
  },
  {
    match: /^\/reporting/,
    config: {
      title: 'Disclosure reports',
      pageDescription:
        'Filing-ready BRSR Principle 6, ISO inventories, ISO 14067 PCF packs, and CBAM registry exports when readiness gates pass.',
    },
  },
  {
    match: /^\/document-upload/,
    config: {
      title: 'Upload bills',
      pageDescription:
        'Upload invoices and utility bills, review duplicates and processing status, and manage document evidence for carbon calculations.',
    },
  },
  {
    // Use `/data` exact-ish prefix so `/data-privacy` is not captured here (CI-BUG-032).
    match: /^\/data(?:\/|\?|$)|^\/document-management/,
    config: {
      title: 'Data',
      pageDescription:
        'Connect Tally, Zoho Books, or QuickBooks and review imported operational transactions for the shared footprint engine.',
    },
  },
  {
    match: /^\/carbon-savings/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'Measured savings, the cut you set, government scheme sites, and offset trading for what remains.',
    },
  },
  {
    match: /^\/(carbon-marketplace|carbon-credits|carbon-trading)/,
    config: {
      title: 'Offsets and credits',
      pageDescription:
        'Buy or sell carbon credits through verified Indian Carbon Market offset projects, manage your credits wallet, and retire credits against your footprint.',
    },
  },
  {
    match: /^\/carbon-forecasting/,
    config: {
      title: 'Forecasting',
      pageDescription:
        'Historical assessments and trends extend into scenario-style drivers informed by your operational and emissions history.',
    },
  },
  {
    match: /^\/ai-advisor|^\/carbon-copilot/,
    config: {
      title: 'AI Advisor',
      pageDescription:
        'Ask organisation sustainability questions — footprint changes, water savings, hotspots, solar ROI, action plans, and cost-effective reduction paths.',
    },
  },
  {
    match: /^\/finance/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'Set a carbon cut and open official government scheme sites. This workspace does not file applications or work with lenders.',
    },
  },
  {
    match: /^\/incentives/,
    config: {
      title: 'Points & Rewards',
      pageDescription:
        'Earn points from assessments, daily tasks, and reduction milestones. Redeem rewards and track ADEETIE-related achievements.',
    },
  },
  {
    match: /^\/adeetie/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'ADEETIE and other public programmes are linked from Reduce and offset → Government schemes.',
    },
  },
  {
    match: /^\/gift-schemes|^\/gift-applications/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'Government energy and MSME programmes are linked from Reduce and offset → Government schemes.',
    },
  },
  {
    match: /^\/green-loans/,
    config: {
      title: 'Reduce and offset',
      pageDescription:
        'Solar and energy-efficiency support is listed under Reduce and offset → Government schemes, including PM-KUSUM and ADEETIE.',
    },
  },
  {
    match: /^\/data-privacy/,
    config: {
      title: 'Data privacy',
      pageDescription:
        'Controls govern how personal or commercial identifiers attached to transactions and documents may be processed.',
    },
  },
  {
    match: /^\/pricing/,
    config: {
      title: 'Pricing & plans',
      pageDescription:
        'Compare five MSME plan tiers with monthly or annual billing, usage-based pricing factors, and enterprise options before you register.',
    },
  },
  {
    match: /^\/billing/,
    config: {
      title: 'Billing and payments',
      pageDescription:
        'View usage-based quotes or fixed monthly and yearly plans, and pay via UPI, net banking, or cards. Payments are informational and do not restrict access.',
    },
  },
  {
    match: /^\/settings/,
    config: {
      title: 'Settings',
      pageDescription:
        'Shortcuts to company profile, data privacy controls, billing, and in-app guidance.',
    },
  },
  {
    match: /^\/information/,
    config: {
      title: 'Information',
      pageDescription:
        'Official software profile, step-by-step user manual, and troubleshooting for common platform issues.',
    },
  },
  {
    match: /^\/why-(carbon-intelligence|sci|ci)/,
    config: {
      title: 'Why Carbon Intelligence?',
      pageDescription:
        'How Carbon Intelligence helps MSMEs measure emissions, meet disclosure expectations, and unlock finance with an India-first workflow.',
    },
  },
  {
    match: /^\/about-us/,
    config: {
      title: 'About us',
      pageDescription:
        'Mission, recognition, and Carbon Intelligence capabilities aligned with Sustainow’s work at sustainow.in.',
    },
  },
  {
    match: /^\/manufacturing-workflow/,
    config: {
      title: 'Manufacturing Workflow',
      pageDescription:
        'Equipment and line-level detail align with emission drivers before assessments run.',
    },
  },
  {
    match: /^\/enterprise\/dashboard/,
    config: {
      title: 'Enterprise Control Center',
      pageDescription:
        'Portfolio-level emissions signals, supplier progress, and compliance readiness across your enterprise program.',
    },
  },
  {
    match: /^\/enterprise\/(profile|registration)/,
    config: {
      title: 'Enterprise Organization',
      pageDescription:
        'Legal entity profile, sites, and registration details that anchor enterprise-wide carbon accounting.',
    },
  },
  {
    match: /^\/enterprise\/workflow/,
    config: {
      title: 'Enterprise Workflow Planner',
      pageDescription:
        'Map compliance processes and operational boundaries before rolling up supplier and site-level inventories.',
    },
  },
  {
    match: /^\/enterprise\/data\/?$/,
    config: {
      title: 'Enterprise Data',
      pageDescription:
        'Import supplier documents and connector feeds, then normalize evidence for enterprise carbon inventories.',
    },
  },
  {
    match: /^\/enterprise\/carbon-footprint/,
    config: {
      title: 'Enterprise Carbon Inventory',
      pageDescription:
        'Scope 1–2 site totals and material scope 3 categories consolidated for enterprise disclosure programs.',
    },
  },
  {
    match: /^\/enterprise\/compliance/,
    config: {
      title: 'Enterprise Compliance',
      pageDescription:
        'BRSR and India-market readiness packs with supplier evidence trails for assurance workflows.',
    },
  },
  {
    match: /^\/enterprise\/reporting/,
    config: {
      title: 'Enterprise Reporting',
      pageDescription:
        'Generate enterprise disclosure PDFs aligned with the same inventory used in compliance reviews.',
    },
  },
  {
    match: /^\/enterprise\/pricing/,
    config: {
      title: 'Enterprise Pricing',
      pageDescription:
        'Compare enterprise plan tiers, usage factors, and billing options before expanding your program.',
    },
  },
];

/**
 * Routes where the global context banner is hidden.
 * Logged-in workspace pages use WorkspacePageHeader locally for a consistent title strip.
 */
export const PAGE_BANNER_HIDDEN_ROUTES = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/privacy-policy',
  '/terms-of-service',
  '/help',
  '/about-us',
  '/insights',
  '/why-carbon-intelligence',
  '/pricing',
  '/admin/login',
  '/dashboard',
  '/msme-profile',
  '/operations',
  '/carbon-twin',
  '/document-upload',
  '/data',
  '/carbon-footprint',
  '/compliance',
  '/reporting',
  '/finance',
  '/carbon-savings',
]);

/** Workspace prefixes that use WorkspacePageHeader (or custom chrome) instead of the global banner. */
const PAGE_BANNER_HIDDEN_PREFIXES = [
  '/msme-profile',
  '/operations',
  '/carbon-footprint',
  '/compliance',
  '/emissions-reduction-targets',
  '/carbon-marketplace',
  '/carbon-savings',
  '/carbon-forecasting',
  '/ai-advisor',
  '/carbon-copilot',
  '/green-loans',
  '/incentives',
  '/gift-',
  '/adeetie',
  '/buyer-audit',
  '/billing',
  '/settings',
  '/information',
  '/data-privacy',
];

export function isPageBannerHidden(pathname: string): boolean {
  const path = pathname || '/';
  if (PAGE_BANNER_HIDDEN_ROUTES.has(path)) {
    return true;
  }
  return PAGE_BANNER_HIDDEN_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function matchRoutePageEntry(pathname: string) {
  return ROUTE_PAGE_INFO_ENTRIES.find((entry) => entry.match.test(pathname));
}

/** Pages that already render the primary h1. The banner title is a label, not a second heading. */
const PAGE_SUPPLIES_PRIMARY_H1 = [
  /^\/partners\/api-documentation\/?$/,
  /^\/partners\/?$/,
  /^\/insights\/[^/]+\/?$/,
  /^\/msme-registration\/?$/,
  /^\/admin\/dashboard(?:\/|$)/,
  /^\/admin\/msme\/[^/]+\/?$/,
  /^\/admin\/integrations(?:\/|$)/,
  /^\/admin\/blog-articles(?:\/|$)/,
  /^\/admin\/emission-factors(?:\/|$)/,
  /^\/admin\/system-health\/?$/,
  /^\/partner\/dashboard(?:\/|$)/,
  /^\/enterprise\/compliance\/?$/,
  /^\/enterprise\/reporting\/?$/,
  /^\/enterprise\/data\/?$/,
  /^\/enterprise\/data-privacy\/?$/,
  /^\/enterprise\/settings\/?$/,
];

/**
 * Real routes whose banner is still the only h1. Unmatched paths render NotFoundPage.
 * /partner is not /partners.
 */
const BANNER_REMAINS_PRIMARY_H1 = [
  '/carbon-passport',
  '/supplier-invite',
  '/admin',
  '/support',
  '/expert',
  '/documents',
  '/recommendations',
  '/enterprise',
  '/partner',
  '/msme-list',
  '/enterprise-program',
  '/blog-articles',
  '/reports',
  '/compliance-hub',
  '/green-finance',
  '/transactions',
];

function pathIsOrUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function bannerTitleIsSecondary(pathname: string): boolean {
  const path = pathname || '/';
  if (PAGE_SUPPLIES_PRIMARY_H1.some((pattern) => pattern.test(path))) {
    return true;
  }
  if (isPageBannerHidden(path) || matchRoutePageEntry(path)) {
    return false;
  }
  if (BANNER_REMAINS_PRIMARY_H1.some((prefix) => pathIsOrUnder(path, prefix))) {
    return false;
  }
  return true;
}

export function resolvePageInfo(pathname: string): PageInfoConfig {
  const path = pathname || '/';
  return matchRoutePageEntry(path)?.config ?? DEFAULT_PAGE_INFO;
}

const DOC_TITLE_FALLBACK = 'Carbon Intelligence';

/**
 * Browser tab title: short primary label plus product suffix for wayfinding across many tabs.
 */
export function resolveDocumentTitle(pathname: string): string {
  const path = pathname || '/';
  const explicit = DOCUMENT_TITLE_BY_PATH[path];
  if (explicit) {
    return `${explicit} · ${DOC_TITLE_FALLBACK}`;
  }
  const matched = matchRoutePageEntry(path);
  const primary = matched ? matched.config.title : 'Page not found';
  return `${primary} · ${DOC_TITLE_FALLBACK}`;
}
